/**
 * Backup Validation Schemas
 * Zod schemas for validating backup file structure
 * Prevents data corruption from malformed backup files
 */
import { z } from 'zod';
/**
 * Valid backup types
 */
export const BackupTypeSchema = z.enum(['full', 'partial', 'incremental']);
/**
 * Valid backup entity types
 */
export const BackupEntityTypeSchema = z.enum([
    'Component',
    'CauseEffect',
    'ComponentFailureLink',
    'FailureCauseLink',
    'FailureCascadeLink',
    'MitigationAction',
    'UserStory',
    'AcceptanceCriteria',
    'FlowNode',
    'FlowEdge',
    'ComponentStoryLink',
    'RPNHistory',
    'AuditLog',
    'Template',
    'Attachment',
    'Note',
    'CustomFieldDefinition',
    'CustomFieldValue',
    'SavedView',
]);
/**
 * Entity counts schema.
 * Open record keyed by PascalCase model name so a comprehensive full backup can
 * report counts for every covered model (not just the original 19).
 */
export const EntityCountsSchema = z.record(z.string(), z.number().int().nonnegative());
/**
 * Backup metadata schema
 */
export const BackupMetadataSchema = z.object({
    version: z.string().min(1).max(20),
    timestamp: z.string().datetime({ message: 'Invalid ISO 8601 timestamp' }),
    source: z.string().min(1).max(100),
    backupType: BackupTypeSchema,
    // Prisma model names (PascalCase). Open string array so newly-covered models
    // in a full backup are accepted, not just the original enumerated types.
    entityTypes: z.array(z.string().min(1)),
    entityCounts: EntityCountsSchema,
    compressionEnabled: z.boolean(),
    includesAttachments: z.boolean(),
    incrementalSinceDate: z.string().datetime().optional(),
    description: z.string().max(1000).optional(),
    createdBy: z.string().max(100).optional(),
}).strict();
/**
 * Attachment schema - validates attachment structure but not data content
 */
export const BackupAttachmentSchema = z.object({
    // IDs are Prisma CUIDs/UUIDs/dbgenerated strings — accept any non-empty id.
    id: z.string().min(1),
    filename: z.string().min(1).max(255),
    originalName: z.string().min(1).max(255),
    mimeType: z.string().min(1).max(100),
    size: z.number().int().nonnegative(),
    path: z.string().min(1).max(500),
    entityType: z.string().min(1).max(50),
    entityId: z.string().min(1),
    description: z.string().max(1000).optional(),
    encoding: z.literal('base64'),
    data: z.string(), // Base64 data - don't validate length, could be large
}).strict();
/**
 * Entity record schema - loose validation for flexibility.
 * Each entity must have at least an id field. IDs are Prisma CUIDs/UUIDs/
 * dbgenerated strings, so we only require a non-empty string (NOT z.uuid(),
 * which rejects real data).
 */
const EntityRecordSchema = z.object({
    id: z.string().min(1),
}).passthrough(); // Allow additional fields
/**
 * Backup data schema.
 * Open record keyed by camelCase Prisma delegate name so a full backup can
 * carry every covered model. Each value is an array of entity records.
 */
export const BackupDataSchema = z.record(z.string(), z.array(EntityRecordSchema));
/**
 * Complete backup schema
 */
export const BackupSchema = z.object({
    metadata: BackupMetadataSchema,
    data: BackupDataSchema,
    attachments: z.array(BackupAttachmentSchema).optional(),
}).strict();
/**
 * Validate backup data and return typed result
 * @throws ZodError if validation fails
 */
export function validateBackup(data) {
    return BackupSchema.parse(data);
}
/**
 * Safe validation that returns result object instead of throwing
 */
export function safeValidateBackup(data) {
    const result = BackupSchema.safeParse(data);
    if (result.success) {
        return { success: true, data: result.data };
    }
    // Format errors for display
    const errors = result.error.errors.map((err) => {
        const path = err.path.join('.');
        return path ? `${path}: ${err.message}` : err.message;
    });
    return { success: false, errors };
}
/**
 * Comprehensive backup validation with warnings
 */
export function validateBackupWithWarnings(data) {
    const warnings = [];
    // First do schema validation
    const schemaResult = safeValidateBackup(data);
    if (!schemaResult.success) {
        return {
            valid: false,
            errors: schemaResult.errors || ['Unknown validation error'],
            warnings,
        };
    }
    const backup = schemaResult.data;
    // Additional semantic validations (warnings only)
    // Check version compatibility
    const version = parseFloat(backup.metadata.version);
    if (version > 1.0) {
        warnings.push(`Backup version ${backup.metadata.version} may not be fully compatible`);
    }
    // Check for empty backup
    const hasData = Object.values(backup.data).some((arr) => arr && arr.length > 0);
    if (!hasData) {
        warnings.push('Backup contains no data');
    }
    // Check entity count consistency.
    // entityCounts is keyed by PascalCase model name; backup.data is keyed by the
    // camelCase delegate name (lower-first of the model name).
    for (const [key, count] of Object.entries(backup.metadata.entityCounts)) {
        const dataKey = key.charAt(0).toLowerCase() + key.slice(1);
        const actualData = backup.data[dataKey];
        if (actualData && actualData.length !== count) {
            warnings.push(`Entity count mismatch for ${key}: metadata says ${count}, data has ${actualData.length}`);
        }
    }
    // Check timestamp is not in the future
    const timestamp = new Date(backup.metadata.timestamp);
    if (timestamp > new Date()) {
        warnings.push('Backup timestamp is in the future');
    }
    return {
        valid: true,
        backup,
        errors: [],
        warnings,
    };
}
//# sourceMappingURL=backup.schema.js.map